import { PreTimeKind, PreTimeSuggestionItem } from '@/types';

export const suggestionItemKey = (item: PreTimeSuggestionItem) =>
  `${item.kind}:${item.referenceId}:${item.code}:${item.supplierCode ?? ''}`;

export const needsFrAssignment = (kind: string) =>
  kind === 'Part' || kind === 'Supplier' || kind === 'Material';

export const isReviewBand = (band: string) =>
  band === 'HighConfidence' || band === 'NeedReview';

export type ExistingFlatRateAction = {
  type?: string;
  referenceId?: number;
  flatRateCode?: string;
};

const sameFlatRateCode = (left?: string, right?: string) => {
  const normalizedLeft = (left ?? '').trim().toUpperCase();
  const normalizedRight = (right ?? '').trim().toUpperCase();
  return normalizedLeft !== '' && normalizedLeft === normalizedRight;
};

export const isFlatRateAlreadyAdded = (
  item: PreTimeSuggestionItem,
  actions: ExistingFlatRateAction[],
) => {
  if (item.kind !== 'FR') return false;
  return actions.some((action) => {
    if (action.type?.toLowerCase() !== 'fr') return false;
    if (
      item.referenceId > 0 &&
      action.referenceId != null &&
      action.referenceId === item.referenceId
    ) {
      return true;
    }
    return sameFlatRateCode(action.flatRateCode, item.code);
  });
};

export const withoutAddedFlatRates = (
  items: PreTimeSuggestionItem[],
  actions: ExistingFlatRateAction[],
) => items.filter((item) => !isFlatRateAlreadyAdded(item, actions));

export const unassignedItemKeys = (
  items: PreTimeSuggestionItem[],
  selectedKeys: Iterable<string>,
  assignments: Record<string, string | undefined>,
) => {
  const selected = new Set(selectedKeys);
  return items
    .filter((item) => {
      const key = suggestionItemKey(item);
      return (
        selected.has(key) && needsFrAssignment(item.kind) && !assignments[key]
      );
    })
    .map(suggestionItemKey);
};

export type ExistingFrAction = {
  id: number;
  type: string;
  code: string;
};

export type AssignmentOption = {
  value: string;
  label: string;
  source: 'existing' | 'new';
  actionId?: number;
  kind?: Extract<PreTimeKind, 'FR' | 'Labour'>;
  referenceId?: number;
};

export const assignmentOptions = (
  selectedItems: PreTimeSuggestionItem[],
  existing: ExistingFrAction[],
): AssignmentOption[] => {
  const created = selectedItems
    .filter((item) => item.kind === 'FR' || item.kind === 'Labour')
    .map((item) => ({
      value: `new:${item.kind}:${item.referenceId}`,
      label: `${item.code}`,
      source: 'new' as const,
      kind: item.kind as Extract<PreTimeKind, 'FR' | 'Labour'>,
      referenceId: item.referenceId,
    }));
  const current = existing
    .filter((action) => {
      const type = action.type?.toLowerCase();
      return type === 'fr' || type === 'labour';
    })
    .map((action) => ({
      value: `existing:${action.id}`,
      label: action.code,
      source: 'existing' as const,
      actionId: action.id,
    }));
  return [...created, ...current];
};

export const resolveAssignmentActionId = (
  option: AssignmentOption | undefined,
  created: { id: number; type: string; referenceId: number }[],
) => {
  if (!option) return undefined;
  if (option.source === 'existing') return option.actionId;
  return created.find(
    (action) =>
      action.type?.toLowerCase() === option.kind?.toLowerCase() &&
      action.referenceId === option.referenceId,
  )?.id;
};
